import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Send, MessageSquare, Users, Building2, Mail, Copy } from "lucide-react";

/** Phase 1 — Targeted Clause Referral compose modal.
 *  Legal picks a contract clause, writes a question, and refers it to an
 *  internal user / role / department, or an external email. Recipients see
 *  only the excerpt + question, never the whole contract. */
const INTERNAL_ROLES = [
  { role: "compliance", label: "Compliance" },
  { role: "records_management", label: "Records Management" },
  { role: "purchasing", label: "Purchasing" },
  { role: "department_director", label: "Business Owner (Dept Director)" },
];

export default function ReferralModal({ open, contract, onClose, onCreated, prefillExcerpt = "" }) {
  const [tab, setTab] = useState("user"); // user | role | department | external
  const [excerpt, setExcerpt] = useState(prefillExcerpt);
  const [question, setQuestion] = useState("");
  const [users, setUsers] = useState([]);
  const [pickedUser, setPickedUser] = useState("");
  const [pickedRole, setPickedRole] = useState("compliance");
  const [department, setDepartment] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [externalUrl, setExternalUrl] = useState("");

  useEffect(() => { if (open) { setExcerpt(prefillExcerpt); setExternalUrl(""); }}, [open, prefillExcerpt]);
  useEffect(() => {
    if (!open) return;
    api.get("/users").then(r => setUsers(r.data || [])).catch(() => setUsers([]));
  }, [open]);

  const submit = async () => {
    if (!excerpt.trim() || !question.trim()) return toast.error("Excerpt and question are required");
    setBusy(true);
    try {
      const payload = {
        excerpt: excerpt.trim(), question: question.trim(),
        recipient_type: tab,
        recipient_user_id: tab === "user" ? pickedUser : null,
        recipient_role: tab === "role" ? pickedRole : null,
        recipient_department: tab === "department" ? department.trim() : null,
        recipient_email: tab === "external" ? email.trim() : null,
        recipient_name: name.trim() || null,
      };
      const r = await api.post(`/contracts/${contract.id}/referrals`, payload);
      toast.success("Referral sent");
      if (tab === "external" && r.data.external_url) {
        const fullUrl = `${window.location.origin}${r.data.external_url}`;
        setExternalUrl(fullUrl);
      } else {
        onCreated && onCreated();
        onClose();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Referral failed"); }
    finally { setBusy(false); }
  };

  const copyExternal = () => {
    navigator.clipboard.writeText(externalUrl);
    toast.success("External link copied");
  };

  if (!open || !contract) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}
         data-testid="referral-modal">
      <div className="bg-white rounded-md w-full max-w-2xl shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#005289]" />
          <div>
            <div className="font-bold text-sm text-slate-900">Refer a clause</div>
            <div className="text-[11px] text-slate-500">
              Contract #{contract.contract_number} · {contract.vendor_legal_name}
            </div>
          </div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4" /></button>
        </div>

        {externalUrl ? (
          <div className="p-5 space-y-3" data-testid="referral-external-link-panel">
            <div className="text-sm">External referral created. Share this <b>excerpt-only</b> link with the external party (expires in 7 days):</div>
            <div className="flex items-center gap-2">
              <input readOnly value={externalUrl} data-testid="referral-external-url"
                     className="flex-1 h-9 px-3 text-xs font-mono border border-slate-200 rounded bg-slate-50" />
              <button onClick={copyExternal} data-testid="referral-copy-url"
                      className="h-9 px-3 text-xs border border-slate-200 rounded flex items-center gap-1">
                <Copy className="w-3 h-3" /> Copy
              </button>
            </div>
            <div className="text-[11px] text-slate-500">
              The link shows only the excerpt + your question. The recipient can respond once via this link.
              All access is audit-logged.
            </div>
            <button onClick={() => { onCreated && onCreated(); onClose(); }}
                    className="ml-auto h-9 px-3 text-xs font-semibold bg-[#005289] text-white rounded">Done</button>
          </div>
        ) : (
        <div className="p-5 space-y-3">
          <div className="flex items-center gap-1 text-xs border-b border-slate-100 pb-1">
            {[
              { k: "user", label: "Internal user", Icon: Users },
              { k: "role", label: "Role", Icon: Users },
              { k: "department", label: "Department", Icon: Building2 },
              { k: "external", label: "External party", Icon: Mail },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)}
                      data-testid={`referral-tab-${t.k}`}
                      className={`px-3 py-1.5 rounded-t border-b-2 flex items-center gap-1 ${tab === t.k ? "border-[#005289] font-bold text-[#005289]" : "border-transparent text-slate-500"}`}>
                <t.Icon className="w-3 h-3" /> {t.label}
              </button>
            ))}
          </div>

          {tab === "user" && (
            <label className="text-xs block">
              <span className="font-bold text-slate-700 block mb-1">Recipient</span>
              <select value={pickedUser} onChange={e => setPickedUser(e.target.value)}
                      data-testid="referral-user"
                      className="w-full h-9 px-3 text-sm border border-slate-200 rounded">
                <option value="">— pick a user —</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name} — {u.role} {u.department ? `· ${u.department}` : ""}</option>
                ))}
              </select>
            </label>
          )}
          {tab === "role" && (
            <label className="text-xs block">
              <span className="font-bold text-slate-700 block mb-1">Role</span>
              <select value={pickedRole} onChange={e => setPickedRole(e.target.value)}
                      data-testid="referral-role"
                      className="w-full h-9 px-3 text-sm border border-slate-200 rounded">
                {INTERNAL_ROLES.map(r => <option key={r.role} value={r.role}>{r.label}</option>)}
              </select>
            </label>
          )}
          {tab === "department" && (
            <label className="text-xs block">
              <span className="font-bold text-slate-700 block mb-1">Department</span>
              <input value={department} onChange={e => setDepartment(e.target.value)}
                     data-testid="referral-department"
                     placeholder="Finance, IT, Compliance…"
                     className="w-full h-9 px-3 text-sm border border-slate-200 rounded" />
            </label>
          )}
          {tab === "external" && (
            <>
              <label className="text-xs block">
                <span className="font-bold text-slate-700 block mb-1">External email</span>
                <input value={email} onChange={e => setEmail(e.target.value)}
                       data-testid="referral-email" type="email"
                       placeholder="counsel@outside.com"
                       className="w-full h-9 px-3 text-sm border border-slate-200 rounded" />
              </label>
              <label className="text-xs block">
                <span className="font-bold text-slate-700 block mb-1">External party name (optional)</span>
                <input value={name} onChange={e => setName(e.target.value)}
                       data-testid="referral-name"
                       placeholder="Outside Counsel Firm"
                       className="w-full h-9 px-3 text-sm border border-slate-200 rounded" />
              </label>
              <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                External referrals generate an <b>excerpt-only</b> signed link that expires in 7 days.
                The recipient never sees the contract number, vendor, or any other detail.
              </div>
            </>
          )}

          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Clause excerpt</span>
            <textarea value={excerpt} onChange={e => setExcerpt(e.target.value)}
                      data-testid="referral-excerpt" rows={5} maxLength={8000}
                      placeholder="Paste the specific section or clause you want reviewed…"
                      className="w-full p-3 text-sm border border-slate-200 rounded resize-none focus:border-[#005289] focus:outline-none font-mono" />
          </label>
          <label className="text-xs block">
            <span className="font-bold text-slate-700 block mb-1">Your question</span>
            <textarea value={question} onChange={e => setQuestion(e.target.value)}
                      data-testid="referral-question" rows={3} maxLength={4000}
                      placeholder="What specifically do you need the recipient to review or confirm?"
                      className="w-full p-3 text-sm border border-slate-200 rounded resize-none focus:border-[#005289] focus:outline-none" />
          </label>
        </div>
        )}

        {!externalUrl && (
        <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
          <button onClick={onClose} className="h-9 px-3 text-xs border border-slate-200 rounded">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="referral-submit"
                  className="ml-auto h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white flex items-center gap-1 disabled:opacity-50">
            <Send className="w-3 h-3" /> {busy ? "Sending…" : "Send referral"}
          </button>
        </div>
        )}
      </div>
    </div>
  );
}
